import { useMemo, useState } from "react";

import KOGCollapse from "../kogCollapse/kogCollapse";
import LoadingComponent from "../loading/loadingComponent";
import PartAttachmentsCollapse from "./partAttachmentsCollapse";

import { QuoteImageResponse } from "@/main/models/response";
import { getTranslateByKey } from "@/main/services/utils";
import { EMPTY_VALUE, PartAttachment, getPartLabel, mapQuoteImageToPartAttachment } from "./evidenceUtils";

import * as S from "./styles";

interface ICollapseEvidenceByPartProps {
	evidences: QuoteImageResponse[];
	isLoading: boolean;
	canDownloadImages?: boolean;
	expanded: boolean;
	reloadTrigger?: number;
	onToggle: (isExpanded: boolean) => void;
	onDeleteEvidence: (evidence: QuoteImageResponse) => void;
	onDownloadEvidence: (url: string, evidence?: QuoteImageResponse) => void;
	onPreview: (index: number, attachments: QuoteImageResponse[]) => void;
}

interface PartAttachmentGroup {
	key: string;
	quoteItemGuid: string;
	title: string;
	part?: PartInfoByQuoteResponse;
	attachments: PartAttachment[];
}

function getGroupTitle(attachment: PartAttachment) {
	return attachment.partInfo ? getPartLabel(attachment.partInfo) : EMPTY_VALUE;
}

function getGroupKey(title: string) {
	return title.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function groupAttachmentsByPart(attachments: PartAttachment[]): PartAttachmentGroup[] {
	const groups = new Map<string, PartAttachmentGroup>();

	attachments.forEach((attachment) => {
		const quoteItemGuid = attachment.quoteItemGuid ?? "";
		if (quoteItemGuid === "") return;

		const title = getGroupTitle(attachment);
		const key = getGroupKey(title);
		const existingGroup = groups.get(key);
		if (existingGroup) {
			existingGroup.attachments.push(attachment);
			return;
		}

		groups.set(key, {
			key,
			quoteItemGuid,
			title,
			attachments: [attachment],
		});
	});

	return Array.from(groups.values()).sort((first, second) =>
		first.title.localeCompare(second.title, undefined, { numeric: true }),
	);
}

function CollapseEvidenceByPart({
	evidences,
	isLoading,
	canDownloadImages = false,
	expanded,
	reloadTrigger,
	onToggle,
	onDeleteEvidence,
	onDownloadEvidence,
	onPreview,
}: ICollapseEvidenceByPartProps) {
	const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);

	const groups = useMemo(() => groupAttachmentsByPart(evidences.map(mapQuoteImageToPartAttachment)), [evidences]);

	function handleToggleSection(isExpanded: boolean) {
		if (!isExpanded) setActiveGroupKey(null);
		onToggle(isExpanded);
	}

	function handleToggleGroup(groupKey: string, isExpanded: boolean) {
		if (isExpanded) {
			setActiveGroupKey(groupKey);
			return;
		}

		setActiveGroupKey((currentKey) => (currentKey === groupKey ? null : currentKey));
	}

	if (isLoading) return <LoadingComponent />;

	if (groups.length === 0) return null;

	return (
		<KOGCollapse
			isOpenCollapseinitial={expanded}
			forceCollapseTrigger={!expanded}
			onCustomCollapse={handleToggleSection}
			header={
				<S.PartSectionHeader id="id-evidence-parts-section">
					{getTranslateByKey("evidence.filesRelatedToParts")}
				</S.PartSectionHeader>
			}
			content={
				<S.PartGroupsContainer>
					{groups.map((group) => (
						<PartAttachmentsCollapse
							key={group.key}
							quoteItemGuid={group.quoteItemGuid}
							title={group.title}
							originPrice={group.part}
							attachments={group.attachments}
							canDownload={canDownloadImages}
							expanded={activeGroupKey === group.key}
							reloadTrigger={reloadTrigger}
							onToggle={(isExpanded) => handleToggleGroup(group.key, isExpanded)}
							onDelete={onDeleteEvidence}
							onDownload={(attachment) => onDownloadEvidence(attachment.externalKey, attachment)}
							onPreview={onPreview}
						/>
					))}
				</S.PartGroupsContainer>
			}
		/>
	);
}

export default CollapseEvidenceByPart;
